import { type ClipboardEvent, type KeyboardEvent, useEffect, useRef } from 'react';
import { cn } from '../lib/cn';
import { useFieldControl } from './Field';

/**
 * Kod jednorazowy (2FA z maila, Figma 08 „Pokaż dostęp”): 6 pól po jednej cyfrze, mono. Wpisanie przechodzi do
 * następnego pola, Backspace do poprzedniego, wklejenie (także „123 456” albo całego zdania z kodem) wypełnia
 * wszystkie pola. Pierwsze pole ma `autocomplete="one-time-code"` i etykietę z `Field`; pozostałe „Cyfra N z 6”.
 * Wartość: same cyfry bez przerw (`value.length` = liczba wypełnionych pól). Klawiatura: grupa to jeden przystanek
 * Tab (pole do wpisania), Tab / Shift+Tab wychodzą z grupy, strzałki przechodzą między cyframi.
 */
export function CodeInput({
  value,
  onChange,
  onComplete,
  length = 6,
  autoFocus = false,
  disabled = false,
  id,
  'aria-describedby': ariaDescribedBy,
}: {
  value: string;
  onChange: (value: string) => void;
  /** wywoływane, gdy wpisano wszystkie cyfry (np. automatyczne „Potwierdź”) */
  onComplete?: (value: string) => void;
  length?: number;
  autoFocus?: boolean;
  disabled?: boolean;
  id?: string;
  'aria-describedby'?: string;
}) {
  const field = useFieldControl({ id, 'aria-describedby': ariaDescribedBy });
  const refs = useRef<Array<HTMLInputElement | null>>([]);
  // bieżąca wartość także między zmianą a ponownym renderem (fokus przechodzi do pola przed renderem)
  const latest = useRef(value);
  latest.current = value;

  useEffect(() => {
    if (autoFocus) refs.current[0]?.focus();
  }, [autoFocus]);

  // po sprawdzeniu (pola były zablokowane) i wyczyszczeniu, np. zły kod: fokus wraca na pierwsze pole
  const wasDisabled = useRef(disabled);
  useEffect(() => {
    if (wasDisabled.current && !disabled && value === '') refs.current[0]?.focus();
    wasDisabled.current = disabled;
  }, [disabled, value]);

  const focusAt = (i: number) => {
    const el = refs.current[Math.max(0, Math.min(i, length - 1))];
    el?.focus();
    el?.select();
  };

  const commit = (next: string, focusIndex: number) => {
    const clean = next.replace(/\D/g, '').slice(0, length);
    latest.current = clean;
    onChange(clean);
    focusAt(focusIndex);
    if (clean.length === length) onComplete?.(clean);
  };

  const insertAt = (i: number, digits: string) => {
    const value = latest.current;
    const start = Math.min(i, value.length);
    const next = value.slice(0, start) + digits + value.slice(start + digits.length);
    commit(next, start + digits.length);
  };

  const handleInput = (i: number, raw: string) => {
    const value = latest.current;
    const digits = raw.replace(/\D/g, '');
    if (!digits) {
      // usunięcie cyfry (zaznaczona i skasowana albo wycięta)
      if (i < value.length) commit(value.slice(0, i) + value.slice(i + 1), i);
      return;
    }
    // pole z cyfrą (bez zaznaczenia) + nowa cyfra = bierzemy nową; wklejenie i autouzupełnienie = całość
    const current = value[i];
    const typed = current && digits.length === 2 && digits.includes(current) ? digits.replace(current, '') : digits;
    insertAt(i, typed);
  };

  const handleKeyDown = (i: number, event: KeyboardEvent<HTMLInputElement>) => {
    const value = latest.current;
    if (event.key === 'Backspace' && !value[i] && i > 0) {
      event.preventDefault();
      commit(value.slice(0, i - 1) + value.slice(i), i - 1);
    } else if (event.key === 'ArrowLeft' && i > 0) {
      event.preventDefault();
      focusAt(i - 1);
    } else if (event.key === 'ArrowRight' && i < Math.min(value.length, length - 1)) {
      event.preventDefault();
      focusAt(i + 1);
    }
  };

  const handlePaste = (i: number, event: ClipboardEvent<HTMLInputElement>) => {
    const digits = event.clipboardData.getData('text').replace(/\D/g, '');
    if (!digits) return;
    event.preventDefault();
    // cały kod wklejony gdziekolwiek = od pierwszego pola
    insertAt(digits.length >= length ? 0 : i, digits.slice(0, length));
  };

  // „roving tabindex”: w kolejności Tab jest tylko pole do wpisania (pierwsze puste, przy pełnym kodzie ostatnie),
  // więc Tab / Shift+Tab wychodzą z grupy do „Zaufaj…”, „Wyślij ponownie”, „Anuluj” (WCAG 2.1.1, 2.4.3);
  // między cyframi strzałki
  const tabStop = Math.min(value.length, length - 1);

  return (
    // biome-ignore lint/a11y/useSemanticElements: grupa pól jednego kodu (fieldset zmieniłby układ formularza)
    <div role="group" aria-label="Kod" className="flex gap-2">
      {Array.from({ length }, (_, i) => (
        <input
          // biome-ignore lint/suspicious/noArrayIndexKey: stała liczba pól, pozycja = tożsamość
          key={i}
          ref={(el) => {
            refs.current[i] = el;
          }}
          tabIndex={i === tabStop ? 0 : -1}
          id={i === 0 ? field.id : undefined}
          aria-label={i === 0 ? undefined : `Cyfra ${i + 1} z ${length}`}
          aria-describedby={i === 0 ? field['aria-describedby'] : undefined}
          aria-invalid={field['aria-invalid']}
          type="text"
          inputMode="numeric"
          autoComplete={i === 0 ? 'one-time-code' : 'off'}
          spellCheck={false}
          disabled={disabled}
          value={value[i] ?? ''}
          onChange={(e) => handleInput(i, e.target.value)}
          onKeyDown={(e) => handleKeyDown(i, e)}
          onPaste={(e) => handlePaste(i, e)}
          onFocus={(e) => {
            // klik w dalsze puste pole = pierwsze puste (bez dziur w kodzie); Tab tu nie trafia (tabIndex -1)
            if (i > latest.current.length) focusAt(latest.current.length);
            else e.target.select();
          }}
          className={cn(
            'h-12 w-11 min-w-0 rounded-md border border-line bg-canvas text-center font-mono text-h4 font-medium text-fg',
            'transition-[border-color,box-shadow] duration-100 outline-none',
            'hover:border-line-strong focus:border-accent focus:shadow-[0_0_0_1px_var(--accent-primary)]',
            'aria-[invalid=true]:border-danger',
            'disabled:cursor-not-allowed disabled:bg-surface disabled:text-fg-muted',
          )}
        />
      ))}
    </div>
  );
}
